/* eslint-disable import/prefer-default-export */
import React from 'react'
import i18n from 'i18n'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { Dialog } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import openHelpPage from 'utils/helpLinkUtil'
import { AutomationId } from 'utils/automationUtil'
import ErrorCode from 'enums/ErrorCode'

const StyledLink = styled.a`
  color: ${({ mode }: { mode: Theme }) => sys[mode].interaction.primary.default};
`

const StyledList = styled.ul`
  margin: 0;
  padding-left: 20px;
`

const StyledListItem = styled.li`
  font-weight: ${sys.fontWeight.semibold};
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
`

const useDialogContents = (statusCode: number | null, users: string[], theme: Theme) => {
  switch (statusCode) {
    case ErrorCode.DASHBOARD_CAN_NOT_ADD_LIVEREPORT_SUBSCRIBER:
      return {
        title: i18n.t('subscribersRemoved', { count: users.length }),
        content: (
          <>
            {i18n.t('subscribersRemovedMsgHeader', { count: users.length })}
            <StyledList>
              {users.map((user) => (
                <StyledListItem key={user}>{user}</StyledListItem>
              ))}
            </StyledList>
            {i18n.t('subscribersRemovedMsgBody', { count: users.length })}
          </>
        )
      }
    case ErrorCode.ADMIN_NO_EXECUTIVE_USER_QUOTA: {
      const limitedUserList = [users[0]]
      return {
        title: i18n.t('liveReportUsersQuotaReached'),
        content: (
          <>
            {i18n.t('liveReportUsersQuotaReachedMsg', { count: limitedUserList.length })}
            <StyledList>
              {limitedUserList.map((user) => (
                <StyledListItem key={user}>{user}</StyledListItem>
              ))}
            </StyledList>
          </>
        ),
        actions: () => openHelpPage(8168252)
      }
    }
    case ErrorCode.PERMISSION_DENIED_INSUFFICIENT_LIVE_REPORT_QUOTA:
      return {
        title: i18n.t('liveReportsQuotaReached'),
        content: (
          <Trans i18nKey="liveReportsQuotaReachedMsg">
            <StyledLink href="mailto:support@quid.com" mode={theme} />
          </Trans>
        )
      }
    case ErrorCode.ADMIN_USER_NOT_FOUND:
      return {
        title: i18n.t('subscribersRemoved_other'),
        content: <>{i18n.t('liveReportSubscribersNotFound')}</>
      }
    default:
      return {
        title: 'Unknown Status Code',
        content: `Unknown status code: ${statusCode}`
      }
  }
}

const WarningDialog = ({
  open,
  onClose,
  statusCode,
  users
}: {
  open: boolean
  onClose: () => void
  statusCode: number | null
  users: string[]
}) => {
  const theme = useDisplayTheme() as Theme
  const { title, content, actions } = useDialogContents(statusCode, users, theme)
  const { t } = useTranslation()

  return (
    <Dialog
      mode={theme}
      open={open}
      onClose={onClose}
      width="600px"
      maxHeight="85vh"
      isStretchedDivider
      testId={AutomationId.liveReportModal.warningDialog}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <Dialog.DialogContent>{content}</Dialog.DialogContent>
      <Dialog.DialogActions
        okButtonProps={{
          children: t('ok'),
          onClick: onClose
        }}
        closeButtonProps={{
          color: 'secondary',
          children: t('learnMore'),
          onClick: actions
        }}
      />
    </Dialog>
  )
}

export default WarningDialog
